/**
 * Хранилище записей «Практики самоподдержки» — ТОЛЬКО на устройстве (13.10.2026).
 *
 * Решение Славы: «хранить только на телефоне, обозначить это в оферте и других
 * юрдокументах». Голос человека — чувствительная запись, и на сервер она не уходит
 * вообще: ни файл, ни текст фраз. Отсюда IndexedDB, а не Supabase.
 *
 * Цена такого решения — запись живёт в одном месте: сменил телефон, переустановил
 * Telegram или почистил данные приложения — она пропала. Экран говорит об этом прямо.
 * ⚠️ Не «улучшать» синхронизацией с сервером без новой редакции документов и согласия.
 */

export type SelfRecording = {
  id: string;
  title: string;
  text: string;
  blob: Blob;
  mime: string;
  seconds: number;
  createdAt: number;
};

const DB = 'slava-self-support';
const STORE = 'recordings';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        t.oncomplete = () => db.close();
      }),
  );
}

export async function listRecordings(): Promise<SelfRecording[]> {
  try {
    const rows = await tx<SelfRecording[]>('readonly', (s) => s.getAll());
    return rows.sort((a, b) => b.createdAt - a.createdAt);
  } catch (e) {
    console.error('[selfSupport] list failed', e);
    return [];
  }
}

export async function saveRecording(r: SelfRecording): Promise<void> {
  await tx('readwrite', (s) => s.put(r));
}

export async function deleteRecording(id: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(id));
}

/** Формат записи, который понимает этот телефон: у iPhone это mp4, у остальных webm */
export function pickRecordingMime(): string {
  const options = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
  const MR = (window as any).MediaRecorder;
  if (!MR?.isTypeSupported) return '';
  return options.find((m) => MR.isTypeSupported(m)) ?? '';
}

export function canRecord(): boolean {
  return !!navigator.mediaDevices?.getUserMedia && typeof (window as any).MediaRecorder !== 'undefined' && typeof indexedDB !== 'undefined';
}
